import React from 'react';
import { Row, Col } from 'reactstrap';
import { Link } from 'react-router-dom';

const PaginationControls = ({
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  showAll,
  handlePageChange,
  handlePageSizeChange,
  goToNextPage,
  goToPreviousPage,
  canPreviousPage,
  canNextPage,
  paginatedDataLength,
}) => {
  // Generate smart page numbers with ellipsis
  const getPageNumbers = () => {
    const delta = 2; // Number of pages to show on each side of current page
    const range = [];
    const rangeWithDots = [];

    // If total pages is 10 or less, show all pages
    if (totalPages <= 10) {
      for (let i = 1; i <= totalPages; i++) {
        range.push(i);
      }
      return range;
    }

    // Always show first page
    range.push(1);

    // Calculate pages around current page
    let start = Math.max(2, currentPage - delta);
    let end = Math.min(totalPages - 1, currentPage + delta);

    // Adjust if we're near the beginning
    if (currentPage <= delta + 2) {
      end = Math.min(2 * delta + 3, totalPages - 1);
    }

    // Adjust if we're near the end
    if (currentPage >= totalPages - delta - 1) {
      start = Math.max(totalPages - 2 * delta - 2, 2);
    }

    // Add ellipsis after first page if needed
    if (start > 2) {
      rangeWithDots.push(1, 'ellipsis-start');
    } else {
      rangeWithDots.push(1);
    }

    // Add pages around current page
    for (let i = start; i <= end; i++) {
      rangeWithDots.push(i);
    }

    // Add ellipsis before last page if needed
    if (end < totalPages - 1) {
      rangeWithDots.push('ellipsis-end', totalPages);
    } else if (totalPages > 1) {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  const pageNumbers = getPageNumbers();

  return (
    <Row className="align-items-center g-3 text-center text-sm-start mt-3 mb-3">
      <Col sm={12} md={6}>
        <label className="d-flex align-items-center">
          <span className="pt-1 fw-normal me-2">Show</span>
          <select
            className="custom-select me-2 custom-select-sm form-control form-control-sm form-select form-select-sm"
            value={showAll ? 'all' : pageSize}
            onChange={(e) => handlePageSizeChange(e.target.value)}
            style={{ 
              width: 'auto',
              minWidth: '80px',
              paddingRight: '2rem',
              paddingLeft: '0.75rem',
              paddingTop: '0.375rem',
              paddingBottom: '0.375rem'
            }}
          >
            {[10, 20, 30, 40, 50].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
            <option value="all">ALL</option>
          </select>
          <span className="pt-1 fw-normal ms-2">entries</span>
        </label>
      </Col>
      <Col sm={12} md={6}>
        <div className="d-flex justify-content-between align-items-center flex-wrap">
          <div className="text-nowrap">
            Showing <span className="fw-normal">{showAll ? totalItems : paginatedDataLength}</span> of{' '}
            <span className="fw-normal">{totalItems}</span> Results
          </div>
          {!showAll && totalPages > 0 && (
            <div className="pagination-wrapper" style={{ 
              maxWidth: '100%', 
              overflowX: 'auto',
              overflowY: 'hidden',
              WebkitOverflowScrolling: 'touch'
            }}>
              <ul className="pagination pagination-separated pagination-md justify-content-center justify-content-sm-start mb-0" style={{ 
                flexWrap: 'nowrap',
                minWidth: 'fit-content'
              }}>
                <li className={!canPreviousPage ? 'page-item disabled' : 'page-item'}>
                  <Link
                    to="#"
                    className="page-link"
                    onClick={(e) => {
                      e.preventDefault();
                      if (canPreviousPage) goToPreviousPage();
                    }}
                  >
                    Previous
                  </Link>
                </li>
                {pageNumbers.map((page, index) => {
                  if (page === 'ellipsis-start' || page === 'ellipsis-end') {
                    return (
                      <li key={`ellipsis-${index}`} className="page-item disabled">
                        <span className="page-link">...</span>
                      </li>
                    );
                  }
                  return (
                    <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                      <Link
                        to="#"
                        className="page-link"
                        onClick={(e) => {
                          e.preventDefault();
                          handlePageChange(page);
                        }}
                      >
                        {page}
                      </Link>
                    </li>
                  );
                })}
                <li className={!canNextPage ? 'page-item disabled' : 'page-item'}>
                  <Link
                    to="#"
                    className="page-link"
                    onClick={(e) => {
                      e.preventDefault();
                      if (canNextPage) goToNextPage();
                    }}
                  >
                    Next
                  </Link>
                </li>
              </ul>
            </div>
          )}
        </div>
      </Col>
    </Row>
  );
};

export default PaginationControls;

